import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Calendar, Award, Users, Globe, TrendingUp, Building } from 'lucide-react';

const TimelineContainer = styled.section`
  padding: 80px 20px;
  background: linear-gradient(135deg, #0a0e27 0%, #1e1b4b 40%, #581c87 100%);
  position: relative;
  overflow: hidden;
`;

const TimelineContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  text-align: center;
  margin-bottom: 60px;
  background: linear-gradient(45deg, #00ff41, #00ffff, #ff0080, #ffff00);
  background-size: 300% 300%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 700;
`;

const TimelineWrapper = styled.div`
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    left: 50%;
    top: 0;
    bottom: 0;
    width: 3px;
    background: linear-gradient(180deg, #87ceeb 0%, #1e3a8a 100%);
    transform: translateX(-50%);
    
    @media (max-width: 768px) {
      left: 30px;
    }
  }
`;

const TimelineItem = styled(motion.div)`
  display: flex;
  align-items: center;
  margin-bottom: 60px;
  position: relative;
  
  &:nth-child(even) {
    flex-direction: row-reverse;
    
    @media (max-width: 768px) {
      flex-direction: row;
    }
  }
  
  @media (max-width: 768px) {
    flex-direction: row;
    margin-left: 60px;
  }
`;

const TimelineItemContent = styled.div`
  flex: 1;
  padding: 0 40px;
  
  @media (max-width: 768px) {
    padding: 0 0 0 20px;
  }
`;

const TimelineCard = styled.div`
  background: rgba(135,206,235,0.1);
  backdrop-filter: blur(15px);
  border: 1px solid rgba(135,206,235,0.3);
  border-radius: 20px;
  padding: 30px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.12);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    border-color: rgba(135,206,235,0.5);
    box-shadow: 0 12px 40px rgba(0,0,0,0.2);
  }
`;

const TimelineIcon = styled.div`
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #87ceeb 0%, #1e3a8a 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2;
  box-shadow: 0 4px 15px rgba(135,206,235,0.3);
  
  @media (max-width: 768px) {
    left: 30px;
    transform: translateX(-50%);
  }
`;

const Year = styled.h3`
  font-size: 1.5rem;
  color: #87ceeb;
  margin-bottom: 10px;
  font-weight: 700;
`;

const Title = styled.h4`
  font-size: 1.3rem;
  color: #ffffff;
  margin-bottom: 15px;
  font-weight: 600;
`;

const Description = styled.p`
  color: rgba(255,255,255,0.8);
  line-height: 1.6;
  margin-bottom: 15px;
`;

const Achievements = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  
  li {
    color: rgba(255,255,255,0.7);
    margin-bottom: 8px;
    padding-left: 20px;
    position: relative;
    
    &::before {
      content: '✓';
      position: absolute;
      left: 0;
      color: #87ceeb;
      font-weight: bold;
    }
  }
`;

const CompanyTimeline = () => {
  const timelineData = [
    {
      year: "2021",
      title: "Company Founded",
      icon: <Building size={24} />,
      description: "InitX Technologies was founded with a vision to create innovative technology solutions for modern businesses.",
      achievements: [
        "Registered as MSME (Micro, Small & Medium Enterprise)",
        "Established headquarters in Sasaram, Bihar",
        "Initial team of 3 engineers",
        "First client project: Local business website"
      ]
    },
    {
      year: "2022",
      title: "First Growth Phase",
      icon: <TrendingUp size={24} />,
      description: "Expanded our service offerings and built a solid client base with quality deliveries.",
      achievements: [
        "Delivered 10+ projects across various industries",
        "Team expansion to 5 professionals",
        "Established cloud hosting partnerships",
        "Revenue growth of 200% year-over-year"
      ]
    },
    {
      year: "2023",
      title: "Digital Solutions Focus",
      icon: <Globe size={24} />,
      description: "Focused on modern web applications and mobile solutions for growing businesses.",
      achievements: [
        "Helped 15+ businesses establish digital presence",
        "Launched MERN stack development services",
        "Achieved SOC 2 Type II compliance",
        "Expanded to serve clients in 5+ countries"
      ]
    },
    {
      year: "2024",
      title: "Innovation & Recognition",
      icon: <Award size={24} />,
      description: "Recognized for quality delivery and innovation in web development solutions.",
      achievements: [
        "Serving 20+ clients globally",
        "Team grew to 8+ professionals",
        "Launched portfolio platform services",
        "Expanded to 8+ regions"
      ]
    },
    {
      year: "2025",
      title: "Architectural Design Services Launch",
      icon: <Building size={24} />,
      description: "Expanded into architectural and civil design services, bridging the gap between technology and construction to offer end-to-end design solutions for residential, commercial, and infrastructure projects.",
      achievements: [
        "Launched full-scale architectural & civil design division",
        "Offering 2D/3D blueprints, CAD drawings & structural planning",
        "Integrated IT consulting with architectural design services",
        "Serving clients across residential, commercial & infrastructure sectors"
      ]
    }
  ];

  return (
    <TimelineContainer>
      <TimelineContent>
        <SectionTitle>Our Journey</SectionTitle>
        <TimelineWrapper>
          {timelineData.map((item, index) => (
            <TimelineItem
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <TimelineIcon>{item.icon}</TimelineIcon>
              <TimelineItemContent>
                <TimelineCard>
                  <Year>{item.year}</Year>
                  <Title>{item.title}</Title>
                  <Description>{item.description}</Description>
                  <Achievements>
                    {item.achievements.map((achievement, i) => (
                      <li key={i}>{achievement}</li>
                    ))}
                  </Achievements>
                </TimelineCard>
              </TimelineItemContent>
            </TimelineItem>
          ))}
        </TimelineWrapper>
      </TimelineContent>
    </TimelineContainer>
  );
};

export default CompanyTimeline;